import styles from "./ProductCard.scss";
import Image from "../image/Image";
import SaveForLaterButton from "../buttons/saveForLaterButton/SaveForLaterButton";
import Spacing, { SPACING } from "../spacing/Spacing";
import ShowOnProductStockStatus from "../conditional/ShowOnProductStockStatus";
import OutOfStockLabel from "../outOfStockLabels/OutOfStockLabel";
import { SECONDARY_LABEL } from "../outOfStockLabels/labelTypes";
import SoldShippedHeader from "../deliveryAndReturns/soldShippedHeader/SoldShippedHeader";
import { TEXT_SIZES } from "../../constants";
import ProductColourForProductCard from "../productColour/ProductColourForProductCard";
import ProductSize from "../productSize/ProductSize";
import VariantSelectionContainer from "../../containers/variantSelection/VariantSelectionContainer";
import classnamesBind from "classnames/bind";
import { fireAfsAnalytics } from "../../analytics/pageInteraction";
import { closeModalAndScroll, isHeroProductWithoutLink } from "./utils";
import {
  GetProductCardLinkParams,
  ProductCardProps,
} from "../../types/components/productCard";

const classnames = classnamesBind.bind(styles);

function getProductCardLink({
  product,
  productLink,
  onClick,
  children,
  dataTestId,
  className,
}: GetProductCardLinkParams): JSX.Element {
  if (isHeroProductWithoutLink({ product, productLink })) {
    return (
      <button
        onClick={(event) => {
          event.preventDefault();
          onClick?.(event);
        }}
        data-testid={dataTestId}
        className={className}
        type={"button"}
      >
        {children}
      </button>
    );
  }

  return (
    <a
      href={productLink ?? undefined}
      data-testid={dataTestId}
      className={className}
    >
      {children}
    </a>
  );
}

export default function ProductCard({
  product,
  productLink,
  handleSaveClick,
  variantIdentifier,
  fireAddToBagAnalytics,
  fireAddToBagErrorAnalytics,
  fireAddToBagMarketing,
  fireNotifyMeClickAnalytics,
  variantSelectorId,
  excludedFromSizeGuideExperiment,
  scope,
  imageWidth,
  primaryImageUrl,
  shouldShowProductControls = true,
  shouldRetainSmallImageSize,
  priceRenderProp,
  ratingRenderProp = () => null,
  labelsRenderProp = () => null,
  modalControls,
}: ProductCardProps): JSX.Element {
  const labels = labelsRenderProp();

  return (
    <section className={styles.container} data-testid="productCard">
      <div className={styles.imageContainer}>
        {getProductCardLink({
          product,
          productLink,
          onClick: (event) => closeModalAndScroll({ modalControls, event }),
          children: (
            <Image
              url={primaryImageUrl}
              alternateText={product.name}
              type="card"
              width={imageWidth}
            />
          ),
          dataTestId: "productCardImageLink",
          className: classnames(styles.imageLink, {
            smallImageOnly: shouldRetainSmallImageSize,
          }),
        })}
        {shouldShowProductControls && (
          <SaveForLaterButton
            handleClick={handleSaveClick}
            productTitle={product.name}
            variantIdentifier={variantIdentifier}
            saveForLaterClass="looksSaveForLater"
          />
        )}
        {labels ? <Spacing size={SPACING.EIGHT}>{labels}</Spacing> : null}
      </div>
      <div className={styles.aside}>
        <Spacing size={SPACING.EIGHT} excludeFirst={true}>
          <ShowOnProductStockStatus productStockStatus={false}>
            <OutOfStockLabel labelStyle={SECONDARY_LABEL} />
          </ShowOnProductStockStatus>
          {getProductCardLink({
            product,
            productLink,
            onClick: (event) => closeModalAndScroll({ modalControls, event }),
            dataTestId: "productCardTitleLink",
            className: styles.link,
            children: <span className={styles.title}>{product.name}</span>,
          })}
          {priceRenderProp()}
          <SoldShippedHeader
            size={TEXT_SIZES.leavesden3}
            fireSellerInfoAnalytics={fireAfsAnalytics}
          />
          {ratingRenderProp()}
          <ProductColourForProductCard />
          <ProductSize size={TEXT_SIZES.leavesden3} />
          {!product?.isDiscontinued ? (
            <Spacing size={SPACING.TWELVE}>
              {shouldShowProductControls && (
                <VariantSelectionContainer
                  isInStock={product.isInStock}
                  shouldShowSave={false}
                  variantSelectorId={variantSelectorId}
                  excludedFromSizeGuideExperiment={
                    excludedFromSizeGuideExperiment
                  }
                  fireNotifyMeClickAnalytics={fireNotifyMeClickAnalytics}
                  fireAddToBagAnalytics={fireAddToBagAnalytics}
                  fireAddToBagErrorAnalytics={fireAddToBagErrorAnalytics}
                  fireAddToBagMarketing={fireAddToBagMarketing}
                  scope={scope}
                  selectedProductId={product.id}
                />
              )}
            </Spacing>
          ) : null}
        </Spacing>
      </div>
    </section>
  );
}
